<script>
import ErrorAlert from "@/components/errorAlert";
import Multiselect from "vue-multiselect";
import axios from "axios";
import validation from "@/helpers/validation";
import Vue from "vue";
import PhoneSelect from "@/components/phoneSelect";

export default {
  props: {
    client: {
      type: Object,
      required: true,
    },
  },
  components: {
    PhoneSelect,
    ErrorAlert,
    Multiselect,
  },
  async created() {
    this.initializeFieldsFromState();
  },
  computed: {
    clientId() {
      return this.$route.params.clientId;
    }
  },
  data() {
    return {
      isEditMode: false,
      registeredAs: 'company',
      forms: {
        personalInfo: {
          name: 'Company Info',
          fields: {
            company_name: {
              name: 'Full company name',
              value: '',
              required: true,
              error: false,
            },
            contact_person: {
              name: 'Contact person',
              value: '',
              required: true,
              error: false,
            },
            vat_number: {
              name: 'VAT number',
              value: '',
              required: true,
              error: false
            },
            company_number: {
              name: 'Registration number',
              value: '',
              required: true,
              error: false
            },
            taxpayer: {
              name: 'VAT (PDV)',
              value: true,
              type: 'radio',
              required: true,
              error: false
            },
            country: {
              name: 'Country',
              value: '',
              options: ['Serbia', 'Bosnia and Herzegovina', 'Slovenia', 'Croatia', 'Montenegro', 'Macedonia'],
              type: 'select',
              error: false,
            },
            city: {
              name: 'City',
              value: '',
              required: true,
              error: false,
            },
            postal_code: {
              name: 'Postal code',
              value: '',
              required: true,
              error: false,
            },
            street_address: {
              name: 'Street address',
              value: '',
              required: true,
              error: false
            },
            phone_number: {
              name: 'Phone',
              value: '',
              type: 'phone',
              required: true,
              error: false,
            },
          }
        },
        bankingInfo: {
          name: 'Banking info',
          fields: {
            bank: {
              name: 'Bank',
              value: '',
              required: true,
              error: false,
            },
            bank_account_number: {
              name: 'Bank account number',
              value: '',
              required: true,
              error: false,
              placeholder: 'ex. 200000000012345600',
              autocomplete: 'cc-number'
            }
          },
        },
      },
    };
  },
  methods: {
    initializeFieldsFromState() {
      this.$store.commit('errorBag/CLEAR');

      // eslint-disable-next-line no-unused-vars
      for (let [formKey, formValue] of Object.entries(this.forms)) {
        for (let [inputKey, inputValue] of Object.entries(formValue.fields)) {
          inputValue.value = this.client[inputKey];
        }
      }
    },
    async complete () {
      this.$store.commit('errorBag/CLEAR');

      for (let form in this.forms) {
        this.validate(form)
      }

      try{
        this.$store.commit('loader/START_LOADING');

        const requestData = {
          registered_as: this.registeredAs,
        }

        // eslint-disable-next-line no-unused-vars
        for (let [formKey, formValue] of Object.entries(this.forms)) {
          for (let [inputKey, inputValue] of Object.entries(formValue.fields)) {
            requestData[inputKey] = inputValue.value
          }
        }
        const { data } = await axios.put(`/admin/client/${this.clientId}/profile`, requestData);
        this.$emit('update:client', data);

        Vue.swal({
          position: "top",
          icon: "success",
          title: "Saved!",
          showConfirmButton: false,
          timer: 1000
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      } finally{
        this.$store.commit('loader/END_LOADING');
      }
    },
    cancel() {
      this.initializeFieldsFromState();
    },
    validateForms() {
      let isValid = true
      for (let form in this.forms) {
        isValid = isValid && this.validate(form);
      }

      return isValid;
    },
    validate(formName) {
      let isValid = true;
      let fields = this.forms[formName].fields;
      for (let field in this.forms[formName].fields) {

        // check if field is empty
        if(fields[field].required && fields[field].value === '') {
          fields[field].error = true;
          isValid = false;
        }

        // check special type of fields
        if(fields[field].type && validation[fields[field].type] !== undefined) {
          if(!validation[fields[field].type](fields[field].value)) {
            fields[field].error = true;
            isValid = false;
          }
        }
      }

      return isValid
    },
  }
}
</script>

<template>
  <form ref="client-profile" autocomplete="on" @submit.prevent="complete">
    <error-alert></error-alert>

    <div class="text-right pr-3">
      <b-form-checkbox switch size="lg" v-model="isEditMode">Edit mode is {{isEditMode ? 'enabled' : 'disabled'}}</b-form-checkbox>
    </div>

    <div v-for="(form, key)  in forms" :key="key">
      <div class="p-3">
        <h3 class="mb-4">{{ form.name }}</h3>
        <div class="row">
          <div class="col-12">
            <div>
              <div v-for="(field, fieldKey) in form.fields" :key="fieldKey"
                   class="form-group row mb-3">
                <label class="col-12 col-md-5 col-form-label" :for="fieldKey">{{ field.name }}:</label>
                <div class="col-12 col-md-7">
                  <div v-if="isEditMode">
                    <b-form-radio-group class="mb-2" v-if="field.type === 'radio'" v-model="field.value">
                      <b-form-radio :value="true" class="mb-1">Yes</b-form-radio>
                      <b-form-radio :value="false" class="mb-1">No</b-form-radio>
                    </b-form-radio-group>
                    <multiselect  v-else-if="field.type === 'select'"
                                  class="text-capitalize"
                                  :class="{ invalid: field.error }"
                                  v-model="field.value"
                                  :options="field.options"
                                  :allow-empty="false"
                                  deselectLabel=""
                                  @input="field.error = false"
                                  :multiple="false">
                      <template slot="option" slot-scope="props">
                        <div class="text-capitalize option__desc"><span class="option__title">{{ props.option }}</span></div>
                      </template>
                    </multiselect>
                    <phone-select
                        v-else-if="field.type === 'phone'"
                        v-model="field.value"
                        @input="field.error = false"
                        :field="field" ></phone-select>
                    <input
                        v-else
                        :id="fieldKey"
                        type="text"
                        class="form-control"
                        @input="field.error = false"
                        :class="{'is-invalid': field.error}"
                        :name="field.name.toLowerCase()"
                        :placeholder="field.placeholder"
                        v-model="field.value"
                    />
                  </div>
                  <div v-else>
                    <p>
                      {{field.value}}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- end col -->
        </div>
      </div>
      <hr>
    </div>

    <div>

    </div>
    <div class="row p-3" v-if="isEditMode">
      <div class="col-md-12 text-right">
        <b-button type="button" variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button type="submit" variant="primary" class="ml-2" >Save</b-button>
      </div>
    </div>
  </form>
</template>

<style scoped>

</style>
